'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProjectBlogs as fetchBlogs } from '../../api/project-listings';
import { BlogCard } from './blog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const ALL_CATEGORIES_TAB = 'all';

export const BlogsList = ({
  locale,
  categories,
}: {
  locale: string;
  categories: { id: number; name: string }[];
}) => {
  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const initialCategoryId = searchParams.get('categoryId');

  const initialCategory = categories.find(category => category.id === Number(initialCategoryId));
  const initialActiveTab = initialCategory ? String(initialCategory.id) : ALL_CATEGORIES_TAB;

  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  useEffect(() => {
    if (initialCategoryId) {
      const category = categories.find(category => category.id === Number(initialCategoryId));
      setActiveTab(category ? String(category.id) : ALL_CATEGORIES_TAB);
    } else {
      setActiveTab(ALL_CATEGORIES_TAB);
    }
  }, [initialCategoryId, categories]);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          sort: '-createdAt',
          page: pageParam,
          results: 7,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <section className={cn('custom-section', 'bg-[#F1F6F8] pb-24 pt-8 sm:pb-16')}>
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn('custom-container', 'flex flex-col items-start justify-start gap-8')}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-start justify-start gap-2 overflow-scroll bg-transparent',
          )}
        >
          <TabsTrigger
            value={ALL_CATEGORIES_TAB}
            className={cn('ea-blogs__tabs-trigger', 'rounded-[12px] px-6 py-4 shadow-none')}
          >
            {t('text2000')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-blogs__tabs-trigger',
                'rounded-[12px] px-6 py-4 shadow-none transition-all',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={activeTab} className="w-full">
          {blogsQuery.error && <p className="pt-8 ">{t('text2364')}</p>}
          {blogsQuery.isSuccess && blogs.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {blogs.map((blog, index) => (
                <BlogCard key={blog.id} blog={blog} firstBlog={index === 0} headingTag="h2" />
              ))}
            </div>
          )}
          <div
            className={cn('mt-8 flex w-full items-center justify-center', {
              hidden: !blogsQuery.hasNextPage,
            })}
          >
            <Button
              onClick={() => blogsQuery.fetchNextPage()}
              isLoading={blogsQuery.isFetchingNextPage}
              aria-label={t('text338')}
              className="w-full sm:w-fit"
              variant={'dark'}
            >
              {t('text338')}
            </Button>
          </div>
        </TabsContent>
        {[ALL_CATEGORIES_TAB, ...categories.map(category => String(category.id))]
          .filter(value => value !== activeTab)
          .map(value => <TabsContent key={value} value={value} forceMount hidden />)}
      </Tabs>
    </section>
  );
};
